import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import {
  Container,
  ChartTitle,
  ChartContainer,
  ChartWrapper,
  YAxisLabels,
  ChartDateText,
  TooltipWrapper,
  TooltipLabel,
  TooltipValue,
} from './styles';

export default function EvolutionChart({
  data,
  chartTitle = 'Evolução em 12 meses (Toneladas x Meses)',
}) {
  const formatTooltipValue = value => {
    if (value >= 1000) return `${(value / 1000).toFixed(0)} mil`;
    return String(value);
  };

  const maxValue = React.useMemo(() => {
    if (!data || data.length === 0) return 0;
    return data.reduce((max, item) => {
      const v = typeof item.value === 'number' ? item.value : 0;
      return v > max ? v : max;
    }, 0);
  }, [data]);

  const { yMax, yTicks } = React.useMemo(() => {
    // Nenhum dado: mantém um padrão neutro
    if (!maxValue || maxValue <= 0) {
      return { yMax: 100, yTicks: [0, 33, 66, 100] };
    }

    // Escalas menores para usuários com vendas em dezenas
    if (maxValue <= 50) {
      const yMaxLocal = 60;
      return { yMax: yMaxLocal, yTicks: [0, 20, 40, 60] };
    }

    if (maxValue <= 100) {
      const yMaxLocal = 120;
      return { yMax: yMaxLocal, yTicks: [0, 40, 80, 120] };
    }

    if (maxValue <= 300) {
      const yMaxLocal = 360;
      return { yMax: yMaxLocal, yTicks: [0, 120, 240, 360] };
    }

    if (maxValue <= 600) {
      const yMaxLocal = 600;
      return { yMax: yMaxLocal, yTicks: [0, 200, 400, 600] };
    }

    // Para valores maiores, usamos degraus em milhares semelhantes ao padrão anterior
    const candidates = [1000, 2000, 5000, 10000, 20000, 40000, 60000];
    const chosenMax =
      candidates.find(candidate => maxValue <= candidate) || 60000;

    const step = chosenMax / 3;
    const ticks = [0, step, step * 2, chosenMax];

    return { yMax: chosenMax, yTicks: ticks };
  }, [maxValue]);

  const formatAxisLabel = value => {
    if (value >= 1000) return `${(value / 1000).toFixed(0)} mil`;
    return `${value}`;
  };

  // Mobile: cinco marcas — 0, 3, 6, 9 e último mês (ex.: 03/2025 em série de 12).
  const xTicksMobile = React.useMemo(() => {
    if (!data || data.length === 0) return undefined;
    const n = data.length;
    if (n <= 5) {
      return data.map(d => d.month).filter(Boolean);
    }
    const positions =
      n >= 12
        ? [0, 3, 6, 9, n - 1]
        : [
            0,
            Math.floor((n - 1) / 4),
            Math.floor((n - 1) / 2),
            Math.floor((3 * (n - 1)) / 4),
            n - 1,
          ];
    const labels = positions
      .filter(i => i >= 0 && i < n)
      .map(i => data[i]?.month)
      .filter(Boolean);
    return [...new Set(labels)];
  }, [data]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <TooltipWrapper>
        <TooltipLabel>{label}</TooltipLabel>
        <TooltipValue>{formatTooltipValue(payload[0].value)}</TooltipValue>
      </TooltipWrapper>
    );
  };

  const [isNarrow, setIsNarrow] = React.useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 770,
  );

  React.useEffect(() => {
    const update = () => setIsNarrow(window.innerWidth <= 770);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const chartMargin = isNarrow
    ? { top: 24, right: 8, left: 0, bottom: 18 }
    : { top: 24, right: 16, left: 4, bottom: 16 };
  const categoryGap = isNarrow ? '22%' : '28%';
  /** Mobile: padding mínimo à esquerda (gráfico um pouco mais à esquerda) sem cortar o 1º mês. */
  const xPadding = isNarrow
    ? { left: 8, right: 8 }
    : { left: 0, right: 4 };

  return (
    <>
      <Container>
        <ChartTitle>{chartTitle}</ChartTitle>
        <ChartWrapper>
          <YAxisLabels>
            {yTicks
              .slice()
              .reverse()
              .map(tick => (
                <span key={tick}>{formatAxisLabel(Math.round(tick))}</span>
              ))}
          </YAxisLabels>
          <ChartContainer>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={data}
                margin={chartMargin}
                barCategoryGap={categoryGap}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e0e0e0"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  type="category"
                  padding={xPadding}
                  tick={{
                    fill: '#333333',
                    fontSize: isNarrow ? 10 : 12,
                  }}
                  tickLine={{ stroke: '#e0e0e0' }}
                  axisLine={{ stroke: '#e0e0e0' }}
                  ticks={isNarrow ? xTicksMobile : undefined}
                  interval={0}
                />
                <YAxis hide domain={[0, yMax]} ticks={yTicks} />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: 'rgba(0, 15, 255, 0.05)' }}
                />
                <Bar
                  dataKey="value"
                  fill="#000fff"
                  radius={[4, 4, 0, 0]}
                  barSize={isNarrow ? 22 : 28}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </ChartWrapper>
      </Container>
      <ChartDateText>
        Última atualização em:{' '}
        {new Date().toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })}{' '}
        às{' '}
        {new Date()
          .toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
          })
          .replace(':', 'h ')}
        min
      </ChartDateText>
    </>
  );
}
